/* eslint-disable no-multi-assign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import { base64ToStr, IChatMessage, isEmoji } from '@ibiz-template/core';
import {
  getEditorEmits,
  getInputProps,
  useNamespace,
  useFilterAttribute,
  useUIStore,
} from '@ibiz-template/vue3-util';
import './input.scss';
import { ITextArea } from '@ibiz/model-core';
import { isIos } from '@ibiz-template/runtime';
import { TextBoxEditorController } from '../text-box-editor.controller';

const AISvg = (): VNode => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 16 16'
    preserveAspectRatio='xMidYMid meet'
    focusable='false'
    fill='CurrentColor'
  >
    <g
      id='aae1.Base基础/1.icon图标/2.normal/ai-star'
      stroke-width='1'
      fill-rule='evenodd'
    >
      <path
        d='M5.817 1.53l3.158 8.797h.054v.152l1.443 4.021-1.402.001-1.041-2.982H2.495l-1.03 2.982L0 14.5 4.671 1.533l1.146-.003zm7.86 5.424V14.5h-1.213V6.954h1.212zM5.248 3.549l-2.342 6.778h4.706L5.249 3.55zM13.046 0c.075 0 .147.02.204.071a.318.318 0 01.094.181l.064.273c.097.417.17.727.255.968.084.24.177.4.31.523.134.124.318.218.599.306.281.088.65.166 1.15.265a.358.358 0 01.195.095c.056.057.083.13.083.213a.289.289 0 01-.083.21.362.362 0 01-.197.094c-.528.093-.918.167-1.214.255-.295.088-.485.187-.621.324-.137.138-.23.324-.31.606-.08.283-.145.651-.23 1.147a.329.329 0 01-.093.184.293.293 0 01-.206.075.308.308 0 01-.207-.072.322.322 0 01-.1-.188l-.006-.033c-.085-.486-.149-.845-.228-1.12-.079-.274-.17-.452-.305-.585-.135-.133-.323-.23-.618-.32s-.683-.168-1.21-.273a.353.353 0 01-.2-.096.29.29 0 01-.08-.208c0-.079.023-.153.079-.211a.35.35 0 01.2-.097c.5-.098.869-.176 1.15-.263.282-.087.465-.18.597-.302.132-.12.224-.278.306-.511.082-.236.151-.539.244-.947l.071-.312a.312.312 0 01.102-.183.311.311 0 01.205-.069z'
        id='aae形状结合'
      ></path>
    </g>
  </svg>
);

/**
 * 移动端文本框
 *
 * @description 使用van-field组件，用于数据录入，通过键盘输入字符。支持编辑器类型包含：`移动端文本框`、`移动端多行文本`、`移动端密码框`
 * @primary
 * @editorparams {name:enableshowpwd,parameterType:boolean,defaultvalue:false,description:控制密码框是否可以切换密码的显示与隐藏}
 * @editorparams {name:rows,parameterType:number,defaultvalue:2,description:设置文本域默认显示的行数，输入框为文本域时生效}
 * @editorparams {"name":"triggermode","parameterType":"'blur' | 'input'","defaultvalue":"'blur'","description":"指定编辑器触发 `change` 值变更事件的模式，input: 输入框输入时触发事件，blur：输入框blur时触发事件"}
 * @editorparams {name:readonly,parameterType:boolean,defaultvalue:false,description:设置编辑器是否为只读态}
 * @editorparams {"name":"srfaiappendcurdata","parameterType":"boolean","defaultvalue":false,"description":"在打开AI功能时，该参数用于判断是否传入对象参数，主要用于在请求历史记录时，附加当前参数"}
 * @editorparams {"name":"srfaiappendcurcontent","parameterType":"string","description":"在打开AI功能时，如果该参数存在值，会将其传入编辑内容作为用户消息，主要用于在请求历史记录后，附加当前编辑内容作为用户消息"}
 * @editorparams {"name":"autoquestion","parameterType":"boolean","defaultvalue":true,"description": "在打开AI功能时历史数据最后一个项是用户消息（USER）时是否自动提问，当打开AI行内聊天时是否自动提问"}
 * @editorparams {"name":"autofill","parameterType":"boolean","defaultvalue":false,"description": "用于AI聊天，AI回答完成之后是否触发回填"}
 * @editorparams {"name":"autoclose","parameterType":"{mode:'minimize' | 'close' | 'closetime',duration?:number}","description": "用于AI聊天，在提问完成后，设置AI窗口的自动关闭模式。其中 mode 设为 minimize 时窗口会最小化，设为 close 时窗口会直接关闭，设为 closetime 时窗口会根据 duration 配置的值延时关闭。duration配置单位为秒（s），默认值为 3 秒"}
 * @editorparams {"name":"enablenoaccess","parameterType":"boolean","defaultvalue":"false", "description":"是否启用无权限模式，若启用无权限模式，上传文件夹需拼接'$'字符，也不需要计算下载凭证"}
 * @editorparams {"name":"globaldownloadprifix","parameterType":"boolean","defaultvalue":"false", "description":"是否使用全局文件下载前缀，若启用，则以global作为前缀"}
 * @editorparams {"name":"srfaiappendresource","parameterType":"string", "description":"AI聊天默认附加资源数据"}
 * @editorparams {"name":"srfmode","parameterType":"string", "description":"指定AI聊天自定义模式"}
 * @editorparams {"name":"srfaichunkview","parameterType":"string", "description":"知识切片视图，用于定义AI交谈打开目标知识切片视图"}
 * @editorparams {"name":"srfaichunkentity","parameterType":"string", "description":"知识切片实体，用于定义AI交谈打开知识切片视图数据主键key"}
 * @editorparams {"name":"chunkview","parameterType":"string", "description":"切片视图，用于定义打开目标切片视图"}
 * @editorparams {"name":"chunkentity","parameterType":"string", "description":"切片实体，用于打开切片视图数据主键key"}
 * @editorparams {"name":"openmode",parameterType:'default' | 'top' | 'bottom',description: 用于AI聊天，AI窗口的打开模式，default：默认窗口模式；top：顶部飘窗模式；bottom：底部飘窗模式}
 * @editorparams {"name":"containerheight",parameterType:"number",description: 用于AI聊天飘窗模式时的高度，0到100按照百分比，大于100按照像素处理}
 * @editorparams {"name":"srfaichatcaption","parameterType":"string", "description":"自定义AI交谈框标题"}
 * @ignoreprops overflowMode
 * @ignoreemits infoTextChange
 */
export const IBizInput = defineComponent({
  name: 'IBizInput',
  props: getInputProps<TextBoxEditorController>(),
  emits: getEditorEmits(),
  setup(props, { emit }) {
    const ns = useNamespace('input');
    const c = props.controller;
    const editorModel = c.model;
    const inputRef = ref();

    // 是否显示密码
    const showPassword = ref(false);

    // 搭载平台类型
    const isIosPlatform = isIos();

    // 文本对齐
    const textAlign = ref('left');

    // 是否显示切换明文、暗文密码图标
    let enableshowpwd = false;

    // 文本域默认行数，仅在 textarea 类型下有效
    const rows = ref(2);
    if (editorModel.editorType === 'TEXTAREA_10') {
      rows.value = 10;
    }

    if (c.editorParams.enableshowpwd) {
      enableshowpwd =
        c.editorParams.enableshowpwd === 'true' ||
        c.editorParams.enableshowpwd === 'TRUE';
    }
    if (c.editorParams.rows) {
      rows.value = Number(c.editorParams.rows);
    }

    // 类型
    const type = computed(() => {
      switch (editorModel.editorType) {
        case 'MOBTEXT':
        case 'TEXTBOX':
          return 'text';
        case 'MOBPASSWORD':
        case 'PASSWORD':
          return 'password';
        case 'MOBTEXTAREA':
        case 'TEXTAREA':
          return 'textarea';
        default:
          return 'text';
      }
    });

    const getTextAlign = () => {
      if (inputRef.value) {
        const computedStyle = getComputedStyle(inputRef.value.$el);
        return computedStyle.textAlign;
      }
      return 'left';
    };

    const currentVal = ref<string | number>('');
    let blurCacheValue: string | number | undefined;

    watch(
      () => props.value,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          if (!newVal) {
            currentVal.value = '';
          } else if (isEmoji(`${newVal}`)) {
            currentVal.value = base64ToStr(`${newVal}`);
          } else {
            currentVal.value = newVal;
          }
          blurCacheValue = currentVal.value;
        }
      },
      { immediate: true },
    );

    onMounted(() => {
      if (inputRef.value) {
        textAlign.value = getTextAlign();
      }
    });

    const onEmit = (
      val: string | number | undefined,
      eventName: string = 'blur',
    ) => {
      if (eventName === c.triggerMode) {
        emit('change', val);
      }
    };

    let isDebounce = false;
    let awaitSearch: () => void;
    // 防抖值变更回调
    const debounceChange = debounce(
      (val: string | number) => {
        // 拦截掉blur触发后change
        if (blurCacheValue !== val) {
          onEmit(val, 'input');
        }
        blurCacheValue = val;
        isDebounce = false;
        if (awaitSearch) {
          awaitSearch();
        }
      },
      300,
      { leading: true },
    );

    // 修复ios中文本域换行后光标错位问题，需手动添加空格
    const fixCursorPosition = () => {
      // 只有右对齐才处理
      if (
        type.value === 'textarea' &&
        isIosPlatform &&
        textAlign.value === 'right'
      ) {
        const textarea = inputRef.value.$el.querySelector('textarea');
        if (textarea) {
          const val = textarea.value;
          if (!val.endsWith(' ')) {
            onEmit(`${val} `);
          }
        }
      }
    };
    // 值变更
    const handleChange = (evt: IData) => {
      const val = evt.target.value;
      isDebounce = true;
      if (type.value === 'textarea') {
        currentVal.value = val;
      }
      debounceChange(val);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e && e.code === 'Enter') {
        fixCursorPosition();
        if (isDebounce) {
          awaitSearch = () => {
            inputRef.value.$el.dispatchEvent(e);
          };
        }
      }
    };

    /**
     * blur时马上抛值变更
     * @author lxm
     * @date 2023-03-06 06:36:23
     */
    const onBlur = () => {
      if (blurCacheValue !== props.value) {
        // 兼容ios中文本域换行后光标错位问题
        if (
          type.value === 'textarea' &&
          textAlign.value === 'right' &&
          isIosPlatform &&
          blurCacheValue &&
          (blurCacheValue as string).endsWith(' ')
        ) {
          blurCacheValue = (blurCacheValue as string).slice(0, -1);
        }
        onEmit(blurCacheValue);
      }
      emit('blur');
    };

    // 自动聚焦
    if (props.autoFocus) {
      watch(inputRef, newVal => {
        if (newVal) {
          const inputTag = type.value === 'textarea' ? 'textarea' : 'input';
          const input = newVal.$el.getElementsByTagName(inputTag)[0];
          input.focus();
        }
      });
    }
    const onFocus = () => {
      emit('focus');
    };

    // 清除输入
    const onClear = () => {
      onEmit('', c.triggerMode);
    };

    // 切换明文密码的显示隐藏
    const switchPwd = () => {
      showPassword.value = !showPassword.value;
    };

    let chatInstance: any;

    const onAIClick = async () => {
      const appDataEntityId = (c.model as ITextArea).appDataEntityId;
      if (!appDataEntityId || !c.deACMode) return;
      const { zIndex } = useUIStore();
      const containerZIndex = zIndex.increment();
      chatInstance = await ibiz.aiChatUtil.getAIChat();
      const { containerOptions, chatOptions } =
        await ibiz.aiChatUtil.getEditorExAIChatParams(
          c.editorParams,
          c.context,
          c.params,
          props.data,
          c.deACMode,
          { chatInstance, view: c.view, ctrl: c.ctrl },
        );
      const resourceOptions = await ibiz.aiChatUtil.getAIResourceOptions(
        c.context,
        c.params,
      );
      chatInstance.create({
        resourceOptions,
        containerOptions: {
          zIndex: containerZIndex,
          ...containerOptions,
        },
        chatOptions: {
          context: { ...c.context },
          params: { ...c.params, srfactag: c.deACMode.codeName },
          appDataEntityId,
          ...chatOptions,
          action: (action: string, message: IChatMessage) => {
            if (action === 'backfill') emit('change', message.realcontent);
          },
        },
      });
    };

    return {
      c,
      ns,
      rows,
      type,
      currentVal,
      handleChange,
      handleKeyUp,
      onBlur,
      onFocus,
      onClear,
      inputRef,
      showPassword,
      enableshowpwd,
      switchPwd,
      onAIClick,
    };
  },
  render() {
    const { unitName } = this.c.parent;

    let content = null;
    if (this.readonly && this.type !== 'password') {
      // 只读显示
      content = `${this.currentVal || ''}`;
      // 当有值且单位存在时才显示单位
      if (content && unitName) {
        content += unitName;
      }
    } else {
      // 编辑态显示
      const slots: IData = {};
      if (unitName) {
        slots.extra = () => {
          return <i class={this.ns.e('unit')}>{unitName}</i>;
        };
      }
      if (this.type === 'password' && this.enableshowpwd) {
        slots['right-icon'] = () => {
          return this.showPassword ? (
            <ion-icon
              name='eye-off-outline'
              onClick={this.switchPwd}
            ></ion-icon>
          ) : (
            <ion-icon name='eye-outline' onClick={this.switchPwd}></ion-icon>
          );
        };
      }
      if (this.c.chatCompletion) {
        slots.button = () => {
          return (
            <div
              onClick={this.onAIClick}
              class={this.ns.e('ai-chat')}
              title={ibiz.i18n.t('editor.textBox.openAiChat')}
            >
              {AISvg()}
            </div>
          );
        };
      }

      content = (
        <van-field
          ref='inputRef'
          modelValue={this.currentVal}
          placeholder={this.controller.placeHolder}
          type={
            this.type === 'password' && this.showPassword ? 'text' : this.type
          }
          rows={this.rows}
          inputmode='text'
          onInput={this.handleChange}
          onKeyup={this.handleKeyUp}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          class={this.ns.b('input')}
          disabled={this.disabled}
          readonly={this.readonly}
          autosize={this.type === 'textarea'}
          autocomplete='new-password'
          clearable
          onClear={this.onClear}
          {...useFilterAttribute(this.$attrs)}
        >
          {slots}
        </van-field>
      );
    }

    return (
      <div
        class={[
          this.ns.b(),
          this.ns.is('textarea', Object.is(this.type, 'textarea')),
          this.disabled ? this.ns.m('disabled') : '',
          this.readonly ? this.ns.m('readonly') : '',
        ]}
      >
        {content}
      </div>
    );
  },
});
